/**
 * Business-level voice IDs, decoupled from any single provider's voice
 * naming so the client never has to know whether Gemini or Doubao is
 * currently serving a request. Add a row here (and a per-provider mapping)
 * before exposing a new voice option to clients.
 */

export interface VoiceDefinition {
  displayName: string;
  languageCode: string;
  gemini: string;
  /** Left undefined until the Doubao adapter ships. */
  doubao?: string;
}

export const VOICE_MAP: Record<string, VoiceDefinition> = {
  'zh-female-warm': {
    displayName: '中文女声-温和',
    languageCode: 'zh-CN',
    gemini: 'Kore',
  },
  'zh-male-calm': {
    displayName: '中文男声-沉稳',
    languageCode: 'zh-CN',
    gemini: 'Puck',
  },
  'en-female-warm': {
    displayName: 'English Female - Warm',
    languageCode: 'en-US',
    gemini: 'Kore',
  },
  'ja-female-warm': {
    displayName: '日本語 女性 - やわらかい',
    languageCode: 'ja-JP',
    gemini: 'Kore',
  },
  'ko-female-warm': {
    displayName: '한국어 여성 - 부드러움',
    languageCode: 'ko-KR',
    gemini: 'Kore',
  },
};

export const DEFAULT_VOICE_ID = 'zh-female-warm';

export function resolveVoice(voiceId: string | undefined): {
  voiceId: string;
  definition: VoiceDefinition;
} {
  const resolvedId = voiceId && VOICE_MAP[voiceId] ? voiceId : DEFAULT_VOICE_ID;
  return { voiceId: resolvedId, definition: VOICE_MAP[resolvedId] };
}

/**
 * UI 语言 → 业务音色 ID。
 *
 * 客户端只知道自己当前显示语言，不该去猜供应商音色名，所以映射放在这里，
 * 跟 VOICE_MAP 同一处维护。未覆盖的语言退回默认音色。
 */
const LANGUAGE_VOICE_MAP: Record<string, string> = {
  en: 'en-female-warm',
  'zh-CN': 'zh-female-warm',
  'zh-TW': 'zh-female-warm',
  ja: 'ja-female-warm',
  ko: 'ko-female-warm',
};

export function voiceIdForLanguage(languageCode: string | undefined): string {
  if (!languageCode) return DEFAULT_VOICE_ID;
  return LANGUAGE_VOICE_MAP[languageCode] ?? DEFAULT_VOICE_ID;
}
